import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { UnderaiterMark, LogoIcon } from '@/components/brand/Marks';
import {
  LayoutDashboard, LogOut, ChevronLeft, ChevronRight, ChevronDown,
  Briefcase, DollarSign, Shield, Settings, Search, FileText,
  RefreshCw, AlertTriangle, Network, Scale, Users, Zap, Gauge, Crown, Sliders, Bot, Receipt, ShieldAlert, FolderOpen, BookOpen, Building2
} from 'lucide-react';
import { Radar, Presentation, Database, FlaskConical, GitBranch, PenLine } from 'lucide-react';

// ─── 11 Modules in 3 Groups (per IA Document) ───
const MODULE_GROUPS = [
  {
    label: 'Command',
    modules: [
      { path: '/command', icon: Crown, label: 'Command Center' },
    ],
  },
  {
    label: 'Work',
    modules: [
      { path: '/today', icon: LayoutDashboard, label: 'Today' },
    ],
  },
  {
    label: 'Insurance Ops',
    modules: [
      { path: '/new-business', icon: Search, label: 'New Business' },
      { path: '/underwriting', icon: AlertTriangle, label: 'Underwriting' },
      { path: '/autonomous', icon: Bot, label: 'Autonomous UW' },
      { path: '/rates', icon: Sliders, label: 'Rate Tables' },
      { path: '/renewals', icon: RefreshCw, label: 'Renewals' },
      { path: '/claims', icon: Briefcase, label: 'Claims' },
      { path: '/policies', icon: FileText, label: 'Policies' },
      { path: '/documents', icon: FolderOpen, label: 'Documents' },
      { path: '/signatures', icon: PenLine, label: 'Signatures' },
      { path: '/knowledge', icon: BookOpen, label: 'Knowledge' },
      { path: '/portfolio', icon: Network, label: 'Portfolio' },
    ],
  },
  {
    label: 'Business Ops',
    modules: [
      { path: '/finance', icon: DollarSign, label: 'Finance' },
      { path: '/compliance', icon: Scale, label: 'Compliance' },
      { path: '/market-intel', icon: Radar, label: 'Market Intel', adminOnly: true },
      { path: '/migration', icon: Database, label: 'Migration Toolkit' },
      { path: '/validation', icon: FlaskConical, label: 'Validation Harness' },
      { path: '/demo-studio', icon: Presentation, label: 'Demo Studio' },
      { path: '/distribution', icon: Users, label: 'Distribution' },
      { path: '/maturity', icon: Gauge, label: 'Maturity Index' },
      { path: '/operating-model', icon: GitBranch, label: 'Operating Modes' },
      { path: '/governance', icon: Shield, label: 'AI Governance', adminOnly: true },
      { path: '/security', icon: ShieldAlert, label: 'Security' },
      { path: '/platform-truth', icon: FileText, label: 'Platform Truth', adminOnly: true },
      { path: '/admin', icon: Settings, label: 'Admin', adminOnly: true },
    ],
  },
];

// Broker sees reduced nav
const BROKER_MODULES = [
  { path: '/broker', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/clients', icon: Users, label: 'Clients' },
  { path: '/new-business', icon: Search, label: 'New Submission' },
  { path: '/data-gaps', icon: ShieldAlert, label: 'Data Gaps' },
  { path: '/brokerage', icon: Building2, label: 'My Brokerage' },
  { path: '/policies', icon: FileText, label: 'My Policies' },
  { path: '/claims', icon: Briefcase, label: 'My Claims' },
  { path: '/renewals', icon: RefreshCw, label: 'My Renewals' },
  { path: '/invoices', icon: Receipt, label: 'Invoices' },
];

// An investor sees the numbers and nothing that names a customer. Same allow-list the backend
// enforces — the nav never offers a screen that will only refuse them.
const INVESTOR_MODULES = [
  { path: '/portfolio', icon: Network, label: 'Portfolio' },
  { path: '/finance', icon: DollarSign, label: 'Finance' },
  { path: '/maturity', icon: Gauge, label: 'Maturity Index' },
  { path: '/operating-model', icon: GitBranch, label: 'Operating Modes' },
];

// Roles with a module allow-list (mirrors App.js ROLE_ROUTES + backend core/roles.py); unnamed roles see the full staff nav
const ROLE_NAV = {
  claims_handler: ['/today', '/claims', '/policies', '/documents', '/signatures', '/knowledge', '/compliance'],
  actuary: ['/today', '/rates', '/portfolio', '/policies', '/knowledge', '/governance', '/maturity', '/validation'],
};

const ROLE_LABELS = {
  platform_owner: 'CUO',
  mga_admin: 'MGA Admin',
  underwriter: 'Underwriter',
  claims_handler: 'Claims Handler',
  actuary: 'Actuary',
  broker: 'Broker',
  regulator: 'Regulator · read-only',
  investor: 'Investor · read-only',
};

// Nothing that creates work is offered to a read-only login
const OBSERVER_HIDDEN = ['/new-business', '/demo-studio', '/migration'];

export default function Sidebar({ collapsed, setCollapsed }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState({ 'Work': true, 'Insurance Ops': true, 'Business Ops': true });

  const isBroker = user?.role === 'broker';
  const isInvestor = user?.role === 'investor';
  const isObserver = ['regulator', 'investor'].includes(user?.role);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleGroup = (label) => {
    setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  // Check if current path belongs to a module
  const isModuleActive = (modulePath) => {
    const p = location.pathname;
    return p === modulePath || p.startsWith(modulePath.endsWith('/') ? modulePath : `${modulePath}/`);
  };

  if (isBroker) {
    return (
      <aside data-testid="sidebar-nav"
        className={`fixed left-0 top-0 h-screen flex flex-col z-50 transition-all duration-200 ${collapsed ? 'w-16' : 'w-60'}`}
        style={{ background: '#1A3A5C' }}>
        {/* Broker-branded header */}
        <div className="px-4 py-4 flex items-center justify-between border-b border-white/10">
          {!collapsed && (
            <div>
              <p className="text-xs font-bold text-white">Broker Portal</p>
              <p className="text-[10px] mt-0.5" style={{ color: '#FF9933' }}>underAIter.com</p>
            </div>
          )}
          {collapsed && <LogoIcon size={24} />}
          <button data-testid="sidebar-toggle" aria-label={collapsed ? 'Expand navigation sidebar' : 'Collapse navigation sidebar'} onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded hover:bg-white/5 transition-colors">
            {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-white/70" /> : <ChevronLeft className="w-3.5 h-3.5 text-white/70" />}
          </button>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {BROKER_MODULES.map(mod => (
            <ModuleLink key={mod.path} mod={mod} collapsed={collapsed} isActive={isModuleActive(mod.path)} />
          ))}
        </nav>
        <SidebarFooter user={user} collapsed={collapsed} handleLogout={handleLogout} />
      </aside>
    );
  }

  if (isInvestor) {
    return (
      <aside data-testid="sidebar-nav"
        className={`fixed left-0 top-0 h-screen flex flex-col z-50 transition-all duration-200 ${collapsed ? 'w-16' : 'w-60'}`}
        style={{ background: '#132A20' }}>
        <div className="px-4 py-4 flex items-center justify-between border-b border-white/10">
          {!collapsed && (
            <div>
              <p className="text-xs font-bold text-white">Investor View</p>
              <p className="text-[10px] mt-0.5" style={{ color: '#4ADE80' }}>read-only</p>
            </div>
          )}
          {collapsed && <LogoIcon size={24} />}
          <button data-testid="sidebar-toggle" aria-label={collapsed ? 'Expand navigation sidebar' : 'Collapse navigation sidebar'}
            onClick={() => setCollapsed(!collapsed)} className="p-1 rounded hover:bg-white/5 transition-colors">
            {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-white/70" /> : <ChevronLeft className="w-3.5 h-3.5 text-white/70" />}
          </button>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {INVESTOR_MODULES.map(mod => (
            <ModuleLink key={mod.path} mod={mod} collapsed={collapsed} isActive={isModuleActive(mod.path)} />
          ))}
        </nav>
        <SidebarFooter user={user} collapsed={collapsed} handleLogout={handleLogout} />
      </aside>
    );
  }

  return (
    <aside data-testid="sidebar-nav"
      className={`fixed left-0 top-0 h-screen flex flex-col z-50 transition-all duration-200 ${collapsed ? 'w-16' : 'w-60'}`}
      style={{ background: '#0D1B2A' }}>
      <SidebarHeader collapsed={collapsed} setCollapsed={setCollapsed} />

      {isObserver && !collapsed && (
        <div className="mx-2 mb-1 px-2 py-1.5 rounded-sm" data-testid="sidebar-readonly-note"
          style={{ background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.25)' }}>
          <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: '#4ADE80' }}>Read-only access</p>
          <p className="text-[10px] mt-0.5 leading-snug" style={{ color: 'rgba(255,255,255,0.8)' }}>
            Nothing here can be altered from this login. Identities are masked.
          </p>
        </div>
      )}

      <nav className="flex-1 py-2 px-2 overflow-y-auto">
        {MODULE_GROUPS.map(group => ({ ...group, visible: group.modules
          .filter(mod => !mod.adminOnly || ['platform_owner', 'mga_admin', 'regulator'].includes(user?.role) || (ROLE_NAV[user?.role] || []).includes(mod.path))
          .filter(mod => !isObserver || !OBSERVER_HIDDEN.includes(mod.path))
          .filter(mod => !ROLE_NAV[user?.role] || ROLE_NAV[user?.role].includes(mod.path)) }))
          .filter(group => group.visible.length > 0)
          .map(group => (
          <div key={group.label} className="mb-1">
            {/* Group Header */}
            {!collapsed && (
              <button
                onClick={() => toggleGroup(group.label)}
                className="flex items-center justify-between w-full px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold transition-colors hover:text-white"
                style={{ color: 'rgba(255,255,255,0.72)', fontFamily: 'Georgia, serif' }}
                data-testid={`group-${group.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <span>{group.label}</span>
                <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${expandedGroups[group.label] ? '' : '-rotate-90'}`} />
              </button>
            )}

            {/* Module Links */}
            {(collapsed || expandedGroups[group.label]) && (
              <div className="space-y-0.5 mt-0.5">
                {group.visible.map(mod => (
                  <ModuleLink key={mod.path} mod={mod} collapsed={collapsed} isActive={isModuleActive(mod.path)} />
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      <SidebarFooter user={user} collapsed={collapsed} handleLogout={handleLogout} />
    </aside>
  );
}

function SidebarHeader({ collapsed, setCollapsed }) {
  return (
    <div className="px-4 py-4 flex items-center justify-between border-b border-white/10">
      {!collapsed && (
        <div className="flex items-center gap-2">
          <LogoIcon size={24} />
          <UnderaiterMark size="text-sm" dark />
        </div>
      )}
      {collapsed && <LogoIcon size={24} />}
      <button data-testid="sidebar-toggle" onClick={() => setCollapsed(!collapsed)}
        className="p-1 rounded hover:bg-white/5 transition-colors">
        {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-white/70" /> : <ChevronLeft className="w-3.5 h-3.5 text-white/70" />}
      </button>
    </div>
  );
}

function ModuleLink({ mod, collapsed, isActive }) {
  return (
    <NavLink
      to={mod.path}
      data-testid={`nav-${mod.label.toLowerCase().replace(/\s/g, '-')}`}
      className={`relative flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors duration-150 ${
        isActive
          ? 'text-white font-semibold'
          : 'font-medium hover:text-white hover:bg-white/[0.09]'
      }`}
      style={isActive ? { background: 'rgba(255,255,255,0.12)' } : { color: 'rgba(255,255,255,0.92)' }}
    >
      {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px]" style={{ background: '#00B4D8' }} />}
      <mod.icon className="w-[16px] h-[16px] shrink-0" style={isActive ? { color: '#00B4D8' } : { color: 'rgba(255,255,255,0.85)' }} />
      {!collapsed && <span className="truncate">{mod.label}</span>}
    </NavLink>
  );
}

function SidebarFooter({ user, collapsed, handleLogout }) {
  return (
    <div className="px-3 py-3 border-t border-white/10">
      {!collapsed && (
        <div className="mb-2 px-1">
          <p className="text-xs font-medium text-white/90 truncate">{user?.name}</p>
          <p className="text-[10px] uppercase tracking-wider mt-0.5" style={{ color: '#FF9933' }} data-testid="sidebar-user-title">
            {user?.title || ROLE_LABELS[user?.role] || user?.role}
          </p>
          {user?.title && <p className="text-[9px] text-white/50 mt-0.5">{ROLE_LABELS[user?.role] || user?.role}{user?.brokerage_name ? ` · ${user.brokerage_name}` : ''}</p>}
        </div>
      )}
      <button data-testid="logout-button" onClick={handleLogout}
        className="flex items-center gap-2 text-white/80 hover:text-white text-xs font-medium transition-colors w-full px-1 py-1">
        <LogOut className="w-3.5 h-3.5" />
        {!collapsed && <span>Sign Out</span>}
      </button>
    </div>
  );
}
